import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import AdminProfileModal from '@/pages/admin/components/AdminProfileModal';
import { getPublicSettings } from '@/api/settings';
import { getPublicCms } from '@/api/cms';
import { getPublicSolutions } from '@/api/solution';
import { getPublicTools } from '@/api/tool';
import { claimLoginReward } from '@/api/interaction';

const navLinks = [
  { label: '首页', path: '/' },
  { label: '方案展示', path: '/solutions' },
  { label: '下载工具', path: '/tools' },
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [showAdminProfile, setShowAdminProfile] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const [allowRegister, setAllowRegister] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>(user?.avatarUrl || '');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (siteName) {
      document.title = siteName;
    }
  }, [siteName]);

  useEffect(() => {
    setAvatarUrl(user?.avatarUrl || '');
  }, [user?.avatarUrl]);
  const isPromoted = isLoggedIn && user?.role === 'admin';
  const isTrueSuperAdmin = user?.role === 'super_admin';
  const displayAsAdmin = isTrueSuperAdmin;

  // 每日登录奖励：改为后端真实记账
  useEffect(() => {
    if (!isLoggedIn || !user?.id || user.role !== 'member') return;
    claimLoginReward()
      .then((res) => {
        if (res.claimed) {
          window.dispatchEvent(new CustomEvent('wanxiang_points_updated'));
        }
      })
      .catch((err) => {
        console.error('Failed to claim daily login reward:', err);
      });
  }, [isLoggedIn, user?.id, user?.role]);

  // 监听站点设置同步更新与用户头像变化
  useEffect(() => {
    // 组件挂载时获取公开站点配置信息
    getPublicSettings()
      .then((res) => {
        setAllowRegister(res.allowRegister);
        setSiteName((res as any).siteName || '');
        setLogoPreview((res as any).logoPreview || '');
        window.dispatchEvent(new CustomEvent('wanxiang_settings_updated'));
      })
      .catch((err) => {
        console.error('Failed to fetch public settings in navbar:', err);
      });

    // 1. 同步加载数据库已发布的首页区块内容
    getPublicCms('home_page_content')
      .then((res) => {
        if (res && Object.keys(res).length > 0) {
          window.dispatchEvent(new CustomEvent('wanxiang_page_content_updated'));
        }
      })
      .catch((err) => console.error('Failed to load homepage DB content in navbar:', err));

    // 2. 同步加载数据库已发布的二级页面区块内容
    getPublicCms('sub_page_content')
      .then((res) => {
        if (res && Object.keys(res).length > 0) {
          window.dispatchEvent(new CustomEvent('wanxiang_subpage_content_updated'));
        }
      })
      .catch((err) => console.error('Failed to load subpage DB content in navbar:', err));

    // 3. 同步加载数据库已发布的真实方案列表并写入缓存
    getPublicSolutions()
      .then((res) => {
        if (res && Array.isArray(res.list)) {
          window.dispatchEvent(new CustomEvent('wanxiang_solutions_updated'));
        }
      })
      .catch((err) => console.error('Failed to sync public solutions list in navbar:', err));

    // 4. 同步加载数据库已发布的真实工具列表并写入缓存
    getPublicTools()
      .then((res) => {
        if (res && Array.isArray(res.list)) {
          window.dispatchEvent(new CustomEvent('wanxiang_tools_updated'));
        }
      })
      .catch((err) => console.error('Failed to sync public tools list in navbar:', err));

    return () => {
    };
  }, []);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
    setUserDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-lg overflow-hidden shrink-0">
            {logoPreview ? (
              <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <i className="ri-store-2-line text-white text-sm"></i>
            )}
          </div>
          <span className={`font-bold text-lg tracking-tight transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
            {siteName || '未配置站点'}
          </span>
        </Link>

        {/* 桌面端导航栏链接 */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                location.pathname === link.path
                  ? scrolled
                    ? 'bg-[#0ea5e9]/10 text-[#0ea5e9]'
                    : 'bg-white/20 text-white'
                  : scrolled
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              {/* 管理员快速进入后台按钮 */}
              {(isAdmin || isPromoted) && (
                <button
                  onClick={() => navigate('/admin')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap border ${
                    scrolled
                      ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                      : 'border-white/30 bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <i className="ri-shield-user-line text-sm"></i>
                  进入后台
                </button>
              )}

              {/* 用户头像与下拉操作菜单 */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setUserDropdown(!userDropdown)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${
                    scrolled ? 'hover:bg-gray-100' : 'hover:bg-white/10'
                  }`}
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-sm ${
                      displayAsAdmin
                        ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
                        : isPromoted
                        ? 'bg-gradient-to-br from-[#10b981] to-[#059669] text-white'
                        : 'bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] text-white'
                    }`}>
                      {(user?.name || user?.phone || user?.email || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <span className={`text-sm font-medium whitespace-nowrap ${
                    scrolled ? 'text-gray-800' : 'text-white'
                  }`}>
                    {(displayAsAdmin || isPromoted)
                      ? (user?.name || user?.phone || user?.email || '管理员')
                      : ((user as any)?.daohao || user?.name || user?.phone || user?.email || '会员')}
                  </span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                    userDropdown ? 'rotate-180' : ''
                  } ${scrolled ? 'text-gray-400' : 'text-white/60'}`}></i>
                </button>

                {userDropdown && (
                  <div className="absolute right-0 top-12 w-56 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50">
                    {/* 用户头像与基本角色展示 */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center gap-2.5">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="avatar" className="w-9 h-9 rounded-xl object-cover" />
                        ) : (
                          <div className={`w-9 h-9 flex items-center justify-center rounded-xl font-bold text-white text-sm ${
                            displayAsAdmin
                              ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                              : isPromoted
                              ? 'bg-gradient-to-br from-[#10b981] to-[#059669]'
                              : 'bg-gradient-to-br from-[#0ea5e9] to-[#0284c7]'
                          }`}>
                            {(user?.name || user?.phone || user?.email || 'U')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {(user as any)?.daohao || user?.name || user?.phone || user?.email}
                          </p>
                          {(user as any)?.daohao && (
                            <p className="text-xs text-gray-400 truncate">{user?.name}</p>
                          )}
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                            displayAsAdmin
                              ? 'bg-amber-50 text-amber-600'
                              : isPromoted
                              ? 'bg-[#10b981]/10 text-[#10b981]'
                              : 'bg-[#0ea5e9]/10 text-[#0ea5e9]'
                          }`}>
                            <i className={`text-xs ${ displayAsAdmin ? 'ri-vip-crown-fill' : isPromoted ? 'ri-shield-user-line' : 'ri-user-line' }`}></i>
                            {displayAsAdmin ? '超级管理员' : isPromoted ? '协作管理员' : '会员'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 功能链接菜单项 */}
                    <div className="py-1">
                      {/* 所有登录用户可见的会员/个人中心 */}
                      <Link
                        to="/member"
                        onClick={() => setUserDropdown(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-[#0ea5e9]/10 rounded-lg">
                          <i className="ri-vip-diamond-line text-[#0ea5e9] text-xs"></i>
                        </div>
                        会员中心
                      </Link>
                      <Link
                        to="/member?tab=announcements"
                        onClick={() => setUserDropdown(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-purple-50 rounded-lg">
                          <i className="ri-megaphone-line text-purple-500 text-xs"></i>
                        </div>
                        站内公告
                      </Link>
                      <Link
                        to="/member?tab=points"
                        onClick={() => setUserDropdown(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-amber-50 rounded-lg">
                          <i className="ri-sparkling-line text-amber-500 text-xs"></i>
                        </div>
                        灵石中心
                      </Link>
                      <Link
                        to="/member?tab=favorites"
                        onClick={() => setUserDropdown(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-green-50 rounded-lg">
                          <i className="ri-star-line text-green-500 text-xs"></i>
                        </div>
                        我的收藏
                      </Link>
                      {/* 协作/超级管理员可见的后台管理快捷入口 */}
                      {(isAdmin || isPromoted) && (
                        <>
                          <div className="mx-4 my-1 border-t border-gray-100"></div>
                          <Link
                            to="/admin"
                            onClick={() => setUserDropdown(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-all"
                          >
                            <div className="w-6 h-6 flex items-center justify-center bg-amber-50 rounded-lg">
                              <i className="ri-dashboard-line text-amber-500 text-xs"></i>
                            </div>
                            管理后台
                          </Link>
                        </>
                      )}
                    </div>

                    <div className="border-t border-gray-100 py-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-all"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-red-50 rounded-lg">
                          <i className="ri-logout-box-line text-red-400 text-xs"></i>
                        </div>
                        退出登录
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                登录
              </Link>
              {allowRegister && (
                <Link
                  to="/login?tab=register"
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-[#f97316] text-white hover:bg-[#ea6c0a] transition-all cursor-pointer whitespace-nowrap"
                >
                  免费注册
                </Link>
              )}
            </>
          )}
        </div>

        {/* 移动端菜单切换按钮 */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {showAdminProfile && <AdminProfileModal onClose={() => setShowAdminProfile(false)} />}

      {/* 移动端下拉导航菜单 */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === link.path
                  ? 'bg-[#0ea5e9]/10 text-[#0ea5e9]'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex gap-2 mt-2">
            {isLoggedIn ? (
              <>
                <div className="flex flex-1 gap-2">
                  <Link to="/member" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 cursor-pointer whitespace-nowrap">会员中心</Link>
                  <Link to="/member?tab=announcements" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 text-sm border border-purple-200 bg-purple-50 rounded-lg text-purple-700 cursor-pointer whitespace-nowrap">站内公告</Link>
                </div>
                {(isAdmin || isPromoted) && (
                  <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 text-sm border border-amber-200 bg-amber-50 rounded-lg text-amber-700 cursor-pointer whitespace-nowrap">管理后台</Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex-1 text-center px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  退出登录
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 cursor-pointer whitespace-nowrap">登录</Link>
                {allowRegister && (
                  <Link to="/login?tab=register" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 text-sm bg-[#f97316] text-white rounded-lg cursor-pointer whitespace-nowrap">免费注册</Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
